/**
 * The three pieces of scroll arithmetic the feed depends on, as functions with no DOM in them.
 *
 * Each is three lines and each is the kind of three lines that are wrong in production and
 * right in the head. They are separated from the hooks that call them (`useStickToBottom`,
 * `useOlderChapters`) so they can be tested without a layout engine — jsdom has no scrolling,
 * so a test that went through the hooks would assert nothing about the maths.
 */

/**
 * How close to the bottom still counts as "reading the newest thing".
 *
 * Generous on purpose: a player who has nudged the wheel a notch to see the last line of the
 * ticker has not left the bottom, and treating them as though they had would freeze the feed
 * mid-turn for someone who is watching it.
 */
export const PINNED_THRESHOLD_PX = 160;

export interface ScrollPosition {
  /** `window.scrollY` */
  scrollTop: number;
  /** `window.innerHeight` */
  viewport: number;
  /** `document.documentElement.scrollHeight` */
  content: number;
}

/**
 * Whether the reader is pinned to the bottom, and therefore whether streaming narration is
 * allowed to scroll for them.
 *
 * Auto-scrolling a reader who has scrolled up to re-read chapter 3 is the classic streaming-UI
 * sin, and it is worse here than in a chat app: the thing they scrolled away to read is
 * fifty seconds of someone else's writing that they cannot pause.
 */
export function isPinnedToBottom(position: ScrollPosition): boolean {
  const distance = position.content - (position.scrollTop + position.viewport);
  return distance <= PINNED_THRESHOLD_PX;
}

/**
 * How much of the remaining distance the feed closes each animation frame while it is
 * following streaming narration, and the floor that guarantees it arrives.
 *
 * **The jump was the complaint.** Narration does not arrive at reading speed — it arrives in
 * bursts, a sentence at a time, and a feed that teleports to the new bottom on each burst
 * throws the line the reader is on several hundred pixels up the screen with no motion to
 * follow. Closing a *fraction* of the gap per frame turns the same bursts into one continuous
 * glide: fast when the text has run away, imperceptible when it is trickling, and always
 * moving in the direction the eye is already travelling.
 *
 * 0.14 at 60fps halves the gap in ~5 frames and closes it in ~30 (half a second), which is
 * quick enough that the newest line is never far below the fold and slow enough to read as
 * movement rather than as a cut. The floor is what stops an asymptote: without it the last
 * pixel takes forever and the loop never ends.
 */
export const FOLLOW_EASE = 0.14;

/** @see FOLLOW_EASE */
export const FOLLOW_MIN_STEP_PX = 1;

/**
 * One frame of the follow: where the page should sit next, given where it is and where the
 * bottom now is.
 *
 * Snapping once the gap is inside the floor is what makes the loop terminate, and it is also
 * the whole of the "is it there yet?" test — a caller that gets `to` back is done.
 */
export function followStep(from: number, to: number): number {
  const gap = to - from;
  if (gap <= FOLLOW_MIN_STEP_PX) return to;
  return from + Math.max(FOLLOW_MIN_STEP_PX, gap * FOLLOW_EASE);
}

/**
 * Where to put the scroll position after prepending older chapters above the reader.
 *
 * Prepending content to a document scrolled to offset *n* leaves the reader at offset *n* —
 * which is now a completely different part of the story, further down by exactly the height
 * of what was inserted. Adding that height back is what makes an upward page feel like nothing
 * happened, which is the only acceptable outcome.
 */
export function anchoredScrollTop(
  before: { scrollTop: number; content: number },
  contentAfter: number,
): number {
  return before.scrollTop + (contentAfter - before.content);
}
